/* eslint-disable import/default */

import {createBrowserHistory as createHistory} from 'history';
import {configure} from 'mobx';
import {Provider} from 'mobx-react';
import {Auth0Provider, useAuth0} from '@auth0/auth0-react';
import React, {useRef} from 'react';
import {render} from 'react-dom';
import {Router} from 'react-router-dom';
import {AppContainer} from 'react-hot-loader';

// Polyfills
import 'url-search-params-polyfill';

import App from './components/app/App';
import config from './config/main';
import preloadData from './preloads/index';
import stores, {hotReloadStores} from './stores/index';
import {initThunks, hotReloadThunks} from './thunks/index';
import {enableMobXLogger} from './utils/debugHelper';
import * as googleAnalytics from './utils/googleAnalytics';
import {attachListenersToHistory, registerHistory} from './utils/history';
import {sentryInit} from './utils/sentryHelper';
import {setAuth0Context} from './utils/auth0';

sentryInit();
googleAnalytics.init();

const routerHistory = createHistory();
registerHistory(routerHistory);

googleAnalytics.listen(routerHistory);

if (config.env === 'development') {
  configure({enforceActions: 'observed'});
  preloadData();
}

enableMobXLogger(true);

initThunks();

hotReloadStores(stores);
hotReloadThunks();

const AuthGate = ({children}) => {
  const {isLoading, isAuthenticated, user, getAccessTokenSilently, loginWithRedirect, logout} = useAuth0();
  const listenersAttached = useRef(false);

  if (isLoading) {
    return null;
  }

  setAuth0Context({isAuthenticated, user, getAccessTokenSilently, loginWithRedirect, logout});

  if (!listenersAttached.current) {
    attachListenersToHistory(stores);
    listenersAttached.current = true;
  }

  return children;
};

const renderApplication = (Component) => {
  render(
    <Auth0Provider
      domain={config.auth0.domain}
      clientId={config.auth0.clientId}
      authorizationParams={{
        audience: config.auth0.audience,
        'redirect_uri': config.auth0.redirectUri
      }}
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      cookieDomain={config.auth0.cookieDomain}
    >
      <AppContainer>
        <Provider {...stores}>
          <AuthGate>
            <Router history={routerHistory}>
              <Component />
            </Router>
          </AuthGate>
        </Provider>
      </AppContainer>
    </Auth0Provider>,
    document.getElementById('app')
  );
};

renderApplication(App);
